import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  Monitor, 
  Users, 
  Phone,
  PhoneOff,
  Camera,
  CameraOff,
  StopCircle,
  UserPlus,
  Copy,
  ExternalLink,
  Shield,
  Hash,
  AlertCircle,
  CheckCircle,
  Wifi,
  WifiOff,
  Settings,
  Volume2,
  VolumeX,
  X,
  Play,
  Pause,
  FileText,
  Download,
  Upload
} from 'lucide-react';

const MeetingVideoModal = ({ 
  meeting, 
  isOpen, 
  onClose, 
  onMeetingUpdated,
  isPremium = false,
  currentUser = { name: "Teilnehmer", role: "participant" }
}) => {
  // Video States
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  
  // UI States
  const [activeTab, setActiveTab] = useState('video');
  const [showParticipants, setShowParticipants] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // Clara KI Protocol States
  const [isProtocolActive, setIsProtocolActive] = useState(false);
  const [transcriptionActive, setTranscriptionActive] = useState(false);
  const [protocolText, setProtocolText] = useState('');
  const [currentAgendaItem, setCurrentAgendaItem] = useState(1);
  
  // Admin States
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'moderator';
  
  // Responsive States - DETECT SIDEBAR STATUS
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Refs
  const modalRef = useRef(null);
  
  // Mock participants data
  const [participants, setParticipants] = useState([
    { id: 1, name: currentUser.name, video: isVideoEnabled, audio: isAudioEnabled, isBot: false },
    { id: 2, name: "Clara KI", video: false, audio: false, isBot: true }
  ]);

  // Detect sidebar status from DOM or localStorage
  useEffect(() => {
    const checkSidebarStatus = () => {
      // Method 1: Check if sidebar element exists and is visible
      const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar');
      if (sidebar) {
        const sidebarWidth = sidebar.offsetWidth;
        setSidebarCollapsed(sidebarWidth < 200); // Assume collapsed if width < 200px
        return;
      }
      
      // Method 2: Check localStorage for sidebar state
      const sidebarState = localStorage.getItem('sidebarCollapsed');
      if (sidebarState !== null) {
        setSidebarCollapsed(JSON.parse(sidebarState));
        return;
      }
      
      // Method 3: Check window width as fallback
      setSidebarCollapsed(window.innerWidth < 1024);
    };

    checkSidebarStatus();
    
    // Listen for window resize
    const handleResize = () => checkSidebarStatus();
    window.addEventListener('resize', handleResize);
    
    // Listen for sidebar toggle events
    const handleSidebarToggle = (e) => {
      if (e.detail && typeof e.detail.collapsed === 'boolean') {
        setSidebarCollapsed(e.detail.collapsed);
      }
    };
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
    };
  }, []);

  const handleJoinMeeting = () => {
    const roomName = `weg-meeting-${meeting.id}-${Date.now()}`;
    const jitsiUrl = `https://meet.jit.si/${roomName}`;
    
    // Open Jitsi Meet in new window
    const jitsiWindow = window.open(
      jitsiUrl,
      'jitsi-meet',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    
    if (jitsiWindow) {
      setMeetingStarted(true);
      if (isAdmin) {
        setIsProtocolActive(true);
        setTranscriptionActive(true);
      }
    }
  };

  // Clara KI Protocol Functions
  const startProtocol = () => {
    setIsProtocolActive(true);
    setTranscriptionActive(true);
    setProtocolText(`WEG-Versammlung: ${meeting.title}\nDatum: ${new Date().toLocaleDateString('de-DE')}\nUhrzeit: ${new Date().toLocaleTimeString('de-DE')}\n\n=== PROTOKOLL START ===\n\n`);
  };

  const stopProtocol = () => {
    setIsProtocolActive(false);
    setTranscriptionActive(false);
    setProtocolText(prev => prev + `\n=== PROTOKOLL ENDE ===\nProtokoll erstellt von Clara KI\n`);
  };

  const addProtocolEntry = (text) => {
    const timestamp = new Date().toLocaleTimeString('de-DE');
    setProtocolText(prev => prev + `[${timestamp}] ${text}\n`);
  };

  const nextAgendaItem = () => {
    const nextItem = currentAgendaItem + 1;
    setCurrentAgendaItem(nextItem);
    addProtocolEntry(`TOP ${nextItem}: Neuer Tagesordnungspunkt`);
  };

  const recordDecision = (decision) => {
    addProtocolEntry(`BESCHLUSS: ${decision}`);
  };

  // CORRECTED RESPONSIVE MODAL CLASSES
  const getModalClasses = () => {
    if (isFullscreen) {
      return "fixed inset-0 z-50";
    }
    
    // FIXED: Properly handle sidebar offset
    if (sidebarCollapsed) {
      return "fixed inset-0 z-50 flex items-center justify-center p-4";
    } else {
      // CORRECT: Use left offset instead of inset-0 + ml-64
      return "fixed top-0 right-0 bottom-0 z-50 flex items-center justify-center p-4 left-64";
    }
  };

  const getModalContentClasses = () => {
    if (isFullscreen) {
      return "w-full h-full bg-gray-900 text-white";
    }
    
    if (sidebarCollapsed) {
      return "w-full max-w-6xl h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl";
    } else {
      return "w-full max-w-4xl h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl";
    }
  };

  if (!isOpen) return null;

  return (
    <div className={getModalClasses()}>
      <div className="absolute inset-0 bg-black bg-opacity-75" onClick={onClose}></div>
      
      <div ref={modalRef} className={getModalContentClasses()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {meeting.title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {new Date(meeting.scheduled_at).toLocaleDateString('de-DE')} um {new Date(meeting.scheduled_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            
            {/* Meeting Status */}
            <div className="flex items-center space-x-2">
              <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                meetingStarted 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
              }`}>
                <Circle className={`w-2 h-2 mr-1 ${meetingStarted ? 'fill-green-500' : 'fill-yellow-500'}`} />
                {meetingStarted ? 'Live' : 'Bereit'}
              </div>
              
              {isProtocolActive && (
                <div className="flex items-center px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-full text-xs font-medium">
                  <FileText className="w-3 h-3 mr-1" />
                  Clara KI protokolliert
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Monitor className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video Area */}
          <div className="flex-1 flex flex-col">
            {/* Video Conference Integration */}
            <div className="flex-1 bg-gray-900 relative flex items-center justify-center">
              {!meetingStarted ? (
                <div className="text-center text-white">
                  <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-xl font-semibold mb-2">Video-Konferenz Integration</h3>
                  <p className="text-gray-300 mb-6">Jitsi Meet Integration für WEG-Versammlungen</p>
                  
                  <div className="space-y-4">
                    {/* Admin Controls */}
                    {isAdmin && (
                      <div className="bg-blue-900 bg-opacity-50 rounded-lg p-4 mb-4">
                        <h4 className="text-lg font-semibold mb-3 text-blue-300">Administrator-Funktionen</h4>
                        <div className="flex flex-wrap gap-3 justify-center">
                          <button
                            onClick={() => {
                              handleJoinMeeting();
                              startProtocol();
                            }}
                            className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Versammlung starten
                          </button>
                          <button
                            onClick={startProtocol}
                            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Clara KI Protokoll aktivieren
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Participant Controls */}
                    <div className="flex flex-wrap gap-3 justify-center">
                      <button
                        onClick={() => setIsVideoEnabled(!isVideoEnabled)}
                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                          isVideoEnabled 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                      >
                        {isVideoEnabled ? <Video className="w-4 h-4 mr-2" /> : <VideoOff className="w-4 h-4 mr-2" />}
                        Video {isVideoEnabled ? 'an' : 'aus'}
                      </button>
                      
                      <button
                        onClick={() => setIsAudioEnabled(!isAudioEnabled)}
                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                          isAudioEnabled 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                      >
                        {isAudioEnabled ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
                        Mikro {isAudioEnabled ? 'an' : 'aus'}
                      </button>
                      
                      <button
                        onClick={handleJoinMeeting}
                        className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Konferenz beitreten
                      </button>
                    </div>
                    
                    <div className="bg-blue-900 bg-opacity-30 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center text-blue-300 mb-2">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        <span className="text-sm font-medium">Tipp</span>
                      </div>
                      <p className="text-sm text-blue-200">
                        Die Video-Integration nutzt Jitsi Meet für sichere, browserbasierte Videokonferenzen ohne zusätzliche Software.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-white">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
                  <h3 className="text-xl font-semibold mb-2">Meeting läuft</h3>
                  <p className="text-gray-300 mb-4">Jitsi Meet Fenster ist geöffnet</p>
                  
                  {isAdmin && (
                    <div className="space-y-3">
                      <button
                        onClick={nextAgendaItem}
                        className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors mx-auto"
                      >
                        <Hash className="w-4 h-4 mr-2" />
                        Nächster TOP
                      </button>
                      
                      <button
                        onClick={() => recordDecision("Beschluss angenommen")}
                        className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors mx-auto"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Beschluss protokollieren
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          {(showParticipants || showChat || isProtocolActive) && (
            <div className="w-80 border-l border-gray-200 dark:border-gray-700 flex flex-col">
              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowParticipants(true)}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    showParticipants 
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Teilnehmer
                </button>
                {showChat && (
                  <button
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    Chat
                  </button>
                )}
                {isProtocolActive && (
                  <button
                    className="flex-1 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border-b-2 border-blue-600"
                  >
                    Clara KI Protokoll
                  </button>
                )}
              </div>

              {/* Protocol Content */}
              {isProtocolActive && (
                <div className="flex-1 flex flex-col">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-white">Live-Protokoll</h3>
                      <div className="flex items-center space-x-2">
                        {transcriptionActive && (
                          <div className="flex items-center text-green-600 dark:text-green-400">
                            <Circle className="w-2 h-2 mr-1 fill-current animate-pulse" />
                            <span className="text-xs">Aufzeichnung</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      TOP {currentAgendaItem} • Clara KI protokolliert automatisch
                    </p>
                  </div>
                  
                  <div className="flex-1 p-4 overflow-y-auto">
                    <textarea
                      value={protocolText}
                      onChange={(e) => setProtocolText(e.target.value)}
                      className="w-full h-full resize-none border-none bg-transparent text-sm text-gray-700 dark:text-gray-300 focus:outline-none"
                      placeholder="Clara KI erstellt automatisch das Protokoll..."
                    />
                  </div>
                  
                  <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          const blob = new Blob([protocolText], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `protokoll-${meeting.title}-${new Date().toISOString().split('T')[0]}.txt`;
                          a.click();
                        }}
                        className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Participants List */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Teilnehmer ({participants.length})</h3>
          <div className="flex flex-wrap gap-2">
            {participants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-center space-x-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm"
              >
                <div className={`w-2 h-2 rounded-full ${
                  participant.isBot ? 'bg-blue-500' : 'bg-green-500'
                }`}></div>
                <span className="text-gray-700 dark:text-gray-300">{participant.name}</span>
                {participant.isBot && (
                  <span className="text-xs text-blue-600 dark:text-blue-400">KI</span>
                )}
                <div className="flex space-x-1">
                  {participant.video ? (
                    <Video className="w-3 h-3 text-green-500" />
                  ) : (
                    <VideoOff className="w-3 h-3 text-gray-400" />
                  )}
                  {participant.audio ? (
                    <Mic className="w-3 h-3 text-green-500" />
                  ) : (
                    <MicOff className="w-3 h-3 text-gray-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingVideoModal;

